import { Textarea, TextareaProps } from "@mantine/core";
import React, { useEffect, useRef, useState } from "react";

type TMcTextAreaNew = TextareaProps & {
  dynamicHeight?: boolean;
};

const McTextAreaNew: React.FC<TMcTextAreaNew> = ({
  value,
  onChange,
  onBlur,
  onKeyDown,
  onKeyUp,
  dynamicHeight,
  ...props
}) => {
  const { styles, ...textareaProps } = props;
  const [internalValue, setInternalValue] = useState<string>(String(value) || "");
  let propagateValueOnBlur = useRef(true);

  useEffect(() => {
    setInternalValue(value == null ? "" : String(value));
  }, [value]);

  const areaRef = useRef(null);

  useEffect(() => {
    // TODO: add data-auto-focus

    // remove? Mantine Textarea has its own autoFocus prop
    if (textareaProps.autoFocus != null && textareaProps.autoFocus !== false) {
      areaRef.current?.focus();

      if (areaRef.current != null) {
        const v = areaRef.current.value;
        areaRef.current.value = "";
        areaRef.current.value = v;
      }
    }
  }, []);

  const checkKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    propagateValueOnBlur.current = true;

    if (e.key === "Enter") {
      onKeyDown?.(e);
      if (e.shiftKey === true) {
        (e.target as HTMLElement).blur();
      }
      return;
    }

    if (e.key === "Escape") {
      e.stopPropagation();
      propagateValueOnBlur.current = false;
      setInternalValue(value != null ? value?.toString() : "");
      (e.target as HTMLElement).blur();
      return;
    }

    onKeyDown?.(e as React.KeyboardEvent<HTMLTextAreaElement>);
  };

  const stopEscOnKeyUp = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      e.stopPropagation();
      return;
    }

    onKeyUp?.(e as React.KeyboardEvent<HTMLTextAreaElement>);
  };

  const valueChanged: React.ChangeEventHandler<HTMLTextAreaElement> = (e) => {
    if (onChange != null && e.target.value !== internalValue) {
      onChange(e);
    }

    setInternalValue(e.target.value || "");
  };

  const blurOverride = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    if (
      onBlur != null &&
      e.target.validity.valid === true &&
      propagateValueOnBlur.current === true &&
      (value == null ? "" : String(value)) !== internalValue
    ) {
      onBlur(e);
    }
  };

  const labelStyles = {
    fontWeight: 600,
    fontSize: "var(--mantine-font-size-sm)",
    marginBottom: 0,
  };
  const mantineStyles = styles as Record<string, unknown> | undefined;

  return (
    <Textarea
      {...textareaProps}
      styles={{ ...mantineStyles, label: { ...labelStyles, ...(mantineStyles?.label as object | undefined) } }}
      autosize={textareaProps.autosize ?? dynamicHeight === true}
      minRows={2}
      onChange={valueChanged}
      onBlur={blurOverride}
      onKeyDown={checkKey}
      onKeyUp={stopEscOnKeyUp}
      value={internalValue}
    />
  );
};

export default McTextAreaNew;
